import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import { makeStyles } from 'tss-react/mui';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { validateHttpsOrWwwUrl } from '@AMIEWEB/Order/OrderDetails/helper';
import { checkIsValidDecimal } from '../helper';
import { theme } from 'styles/global-styles';
import ClearIcon from '@mui/icons-material/Clear';
import { useLocation } from 'react-router-dom';
import { selectEditOrderId, selectPage3 } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { missingField } from 'app/constants';
import { selectUser } from 'oidc/user.selectors';
import { IUser } from 'oidc/user.redux';
import { Concatenate } from 'utils/string/string';
import { useSelector } from 'react-redux';
import { convertToFormat } from 'app/helpers/dateHelper';
import { SwitchFormComponent } from '../Common/SwitchFormComponent';

export const vmsCharacterValidation = t => {
  const validation = (
    <>
      <div>{t('order.newOrderCreation.orderDetails.technologyInfo.maxCharacterReached')}</div>
      <div>{t('order.newOrderCreation.orderDetails.technologyInfo.character')}</div>
      <div>{t('order.newOrderCreation.orderDetails.technologyInfo.limit')}</div>
    </>
  );
  return validation;
};

export const getCharacterCount = (value, count, t) => {
  const helperDiv = (
    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      {value?.length === 255 ? (
        <span>{vmsCharacterValidation(t)}</span>
      ) : (
        <span style={{ visibility: 'hidden' }}>placeholder</span>
      )}
      <span>{`${count?.length ?? 0}/255`}</span>
    </div>
  );

  return helperDiv;
};

const useStyles = makeStyles()(theme => ({
  fieldStyle: {
    'padding-bottom': '12px',
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
    '& .MuiInputAdornment-positionEnd': {
      color: `${theme.palette.system.red}`,
    },
  },
  icon: {
    fill: `${theme.palette.system.red}`,
  },
  validUrl: {
    position: 'absolute',
    top: '50%',
    left: '14px',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    pointerEvents: 'auto',
    backgroundColor: 'transparent',
    zIndex: 1,
    fontSize: '14px',
    color: 'transparent',
  },
  boxInlineSubHeadLabelTxt: {
    fontWeight: '500',
    fontSize: '14px',
    paddingBottom: '10px',
  },
  boxInlineLabelTxt: {
    fontSize: '13px',
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    marginLeft: '-16px',
    paddingTop: '1px',
  },
}));

const TechnologyInformation = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { control, setError, clearErrors, getValues, setValue } = useFormContext();
  const { classes } = useStyles();
  const [validUrl, setValidUrl] = React.useState<boolean>(false);
  const [validCharacterCount, setValidCharacterCount] = React.useState<boolean>(false);
  const [vmsOrderNumberFocused, setVmsOrderNumberFocused] = React.useState<boolean>(false);
  const [vmsWebLinkFocused, setVmsWebLinkFocused] = React.useState<boolean>(false);
  const user: IUser = useSelector(selectUser);
  const orderId = useSelector(selectEditOrderId);
  const {
    isBlockVmsSync: isBlockVmsSyncExistingVal,
    blockVmsLastUpdated: blockVmsLastUpdatedExistingVal,
    blockVmsLastUpdatedFirstName: blockVmsLastUpdatedFirstNameExistingVal,
    blockVmsLastUpdatedLastName: blockVmsLastUpdatedLastNameExistingVal,
  } = useSelector(selectPage3);
  const isBlockVmsSyncFormVal: boolean = useWatch({ name: 'isBlockVmsSync' });

  const isOrderEdit = !!orderId || pathname.includes('/edit');

  const handleValidateUrl = value => {
    if (value?.length > 0) {
      const isValid = validateHttpsOrWwwUrl(value);
      setValidUrl(!isValid);
      if (!isValid) {
        setError('technologyInfo.vmsWebLink', { message: t('invalidFormat') });
        return t('invalidFormat');
      } else {
        clearErrors('technologyInfo.vmsWebLink');
      }
    } else {
      if (value === '' && value.length === 0) {
        setValidUrl(false);
      }
    }
  };

  const handleCharacterCount = value => {
    if (value?.length === 256) {
      setValidCharacterCount(true);
      return t('order.newOrderCreation.orderDetails.technologyInfo.maxCharacterReached');
    } else {
      setValidCharacterCount(false);
    }
  };

  const handleTextClick = inputValue => event => {
    event.stopPropagation();
    if (inputValue) {
      const url = inputValue.startsWith('https://') ? inputValue : `https://${inputValue}`;
      window.open(url, '_blank');
    }
  };

  const handleClearInput = () => {
    setValue('webLink', '');
    setValidUrl(false);
  };

  return (
    <>
      <Grid container columnSpacing={2} marginTop={0}>
        <Grid item xs={4}>
          <Controller
            name={'VMSOrderNumber'}
            rules={{ validate: handleCharacterCount }}
            control={control}
            render={({ field }) => (
              <SingleLineInput
                id={'VMSOrderNumber'}
                inputDefaultValue={field.value}
                inputLabel={t('order.newOrderCreation.orderDetails.technologyInfo.vmsReq')}
                inputName={'VMSOrderNumber'}
                inputValue={field.value}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                }}
                fieldStyle={classes.fieldStyle}
                inputError={field?.value?.length === 255 ? true : false}
                helperText={getCharacterCount(field?.value, getValues('VMSOrderNumber'), t)}
                handleWarning={() => setVmsOrderNumberFocused(true)}
                handleBlur={() => setVmsOrderNumberFocused(false)}
                shrinkLabel={field.value || vmsOrderNumberFocused}
                InputProps={{
                  endAdornment: validCharacterCount && (
                    <InputAdornment position="end">
                      <IconButton color="error" edge="end" size="medium">
                        <ErrorOutline />
                      </IconButton>
                    </InputAdornment>
                  ),
                  classes: {
                    icon: classes.icon,
                  },
                }}
                inputProps={{ maxLength: 255 }}
              />
            )}
          />
        </Grid>
        <Grid item xs={8}>
          <Controller
            name={'webLink'}
            rules={{ validate: handleValidateUrl }}
            control={control}
            render={({ field }) => (
              <Box position="relative" display="inline-block" width="100%">
                <SingleLineInput
                  id={'webLink'}
                  inputDefaultValue={field?.value}
                  inputLabel={t('order.newOrderCreation.orderDetails.technologyInfo.vmsWebLink')}
                  inputName={'webLink'}
                  inputValue={field?.value}
                  handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event.target.value);
                  }}
                  handleWarning={() => setVmsWebLinkFocused(true)}
                  handleBlur={() => setVmsWebLinkFocused(false)}
                  shrinkLabel={field.value || vmsWebLinkFocused}
                  fieldStyle={classes.fieldStyle}
                  inputError={validUrl}
                  helperText={validUrl && t('invalidFormat')}
                  InputProps={{
                    sx: {
                      color: !validUrl ? `${theme.palette.system.paleDarkBlue} !important` : undefined,
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        {validUrl && field.value && (
                          <IconButton color="error" edge="end" size="medium">
                            <ErrorOutline />
                          </IconButton>
                        )}
                        {field.value && (
                          <Box position="relative" zIndex={20}>
                            <IconButton
                              color="default"
                              edge="end"
                              size="small"
                              sx={{ paddingLeft: '12px' }}
                              onClick={handleClearInput}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </InputAdornment>
                    ),
                    classes: {
                      icon: classes.icon,
                    },
                  }}
                />
                {field.value && !validUrl && (
                  <span onClick={handleTextClick(field?.value)} className={classes.validUrl}>
                    {field?.value.length > 55 ? `${field.value.substring(0, 50)}` : field.value}
                  </span>
                )}
              </Box>
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Controller
          name={'VMSBillRate'}
          control={control}
          render={({ field: { onChange, ...rest } }) => (
            <SingleLineInput
              id={'VMSBillRate'}
              inputDefaultValue={rest.value}
              inputLabel={t('order.createUnit.technologyInformation.vmsBillDescription')}
              inputName={'VMSBillRate'}
              inputValue={rest.value}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target;
                const newValue = value.replace(/[^0-9.-]/g, '');
                if (checkIsValidDecimal(newValue)) {
                  onChange(newValue);
                }
              }}
              fieldStyle={classes.fieldStyle}
              startAdornmentValue="$"
              handleBlur={() => onChange(isNaN(parseFloat(rest.value)) ? null : parseFloat(rest.value).toFixed(2))}
              showStartAdornment={true}
            />
          )}
        />
      </Grid>
      {/* Bulk VMS Sync */}
      {isOrderEdit ? (
        <>
          <Grid container columnSpacing={2} alignItems="center">
            <Grid item xs={4} marginTop={2}>
              <Typography classes={{ root: classes.boxInlineSubHeadLabelTxt }}>
                {t('order.createUnit.technologyInformation.blockVMSSync')}
              </Typography>
            </Grid>
            <Grid item xs={8} marginTop={1}>
              <SwitchFormComponent
                name="isBlockVmsSync"
                label={''}
                checkedValue={isBlockVmsSyncExistingVal ?? false}
                onText={t('order.orderDetails.buttonConfirmation.Yes')}
                offText={t('order.orderDetails.buttonConfirmation.No')}
                switchRowStyle={classes.switchRow}
              />
            </Grid>
          </Grid>
          <Grid container columnSpacing={2} marginTop={1}>
            <Grid item xs={4} >
              <Typography sx={{ fontWeight: '500' }} classes={{ root: classes.boxInlineLabelTxt }}>
                {t('order.createUnit.technologyInformation.lastUpdatedBy')}
              </Typography>
            </Grid>
            <Grid item xs={6} >
              <Typography sx={{ fontWeight: '400' }} classes={{ root: classes.boxInlineLabelTxt }}>
                {
                  getLastUpdatedInfo({
                    user,
                    lastUpdatedUserNameExistingVal:
                      blockVmsLastUpdatedFirstNameExistingVal || blockVmsLastUpdatedLastNameExistingVal
                        ? Concatenate(
                            [blockVmsLastUpdatedFirstNameExistingVal, blockVmsLastUpdatedLastNameExistingVal],
                            ' ',
                          )
                        : missingField,
                    existingVal: isBlockVmsSyncExistingVal,
                    currVal: isBlockVmsSyncFormVal,
                  }).lastUpdatedBy
                }
              </Typography>
            </Grid>
          </Grid>
          <Grid container columnSpacing={2} marginTop={1}>
            <Grid item xs={4} >
              <Typography sx={{ fontWeight: '500' }} classes={{ root: classes.boxInlineLabelTxt }}>
                {t('order.createUnit.technologyInformation.lastUpdatedDate')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ fontWeight: '400' }} classes={{ root: classes.boxInlineLabelTxt }}>
                {
                  getLastUpdatedInfo({
                    existingVal: isBlockVmsSyncExistingVal,
                    currVal: isBlockVmsSyncFormVal,
                    lastUpdatedDateExistingVal: convertToFormat(
                      blockVmsLastUpdatedExistingVal,
                      'MM/DD/YYYY h:mm A',
                      true,
                    ),
                  }).lastUpdatedDate
                }
              </Typography>
            </Grid>
          </Grid>
        </>
      ) : (
        <></>
      )}
    </>
  );
};

export default TechnologyInformation;
const getLastUpdatedInfo = ({
  user,
  lastUpdatedUserNameExistingVal = missingField,
  lastUpdatedDateExistingVal = missingField,
  existingVal,
  currVal,
}: {
  user?: IUser;
  lastUpdatedUserNameExistingVal?: string | null;
  lastUpdatedDateExistingVal?: string | null;
  existingVal: boolean | null;
  currVal: boolean | null;
}) => {
  const isValChanged = !!currVal !== !!existingVal;
  const lastUpdatedBy = `${user?.userInfo?.firstName} ${user?.userInfo?.lastName}`;
  return {
    lastUpdatedBy: isValChanged ? lastUpdatedBy : lastUpdatedUserNameExistingVal ?? missingField,
    lastUpdatedDate: isValChanged
      ? convertToFormat(new Date(), 'MM/DD/YYYY h:mm A', true)
      : lastUpdatedDateExistingVal ?? missingField,
  };
};
